import { Icon } from '../components/Icon.js';
import type { Message, MessageId } from '../fixtures/domain.js';
import styles from './MessageQuote.module.css';

export interface QuotedPreview {
  id: MessageId;
  /** "You", the candidate's name, or absent for a system note nobody wrote. */
  authorName?: string;
  text: string;
  /** Staged in the composer to be edited, not answered. */
  editing?: boolean;
}

/**
 * A message reduced to what a quote shows of it, in one place, because the
 * thread and the composer both need it and a quote that described a message
 * differently in the two would be worse than either.
 */
export function toQuotedPreview(message: Message, authorName?: string): QuotedPreview {
  return {
    id: message.id,
    ...(authorName ? { authorName } : {}),
    // A wordless message would show an empty quote and read as a broken one, so
    // something recognisable stands in: the name of the form it carried (the
    // picker's note is optional, so note-less invites are ordinary), or an
    // attachment's filename — the mock's `wordsOf` rule.
    text:
      message.text !== ''
        ? message.text
        : (message.form?.name ??
          (message.attachments?.[0]?.voice ? 'Voice note' : message.attachments?.[0]?.name) ??
          ''),
  };
}

/**
 * Who to name as the author of a quote, in one place because three callers ask:
 * the thread rendering a quote in a bubble, and each surface staging one in its
 * composer. `undefined` means "nobody wrote this" — the system-note case, which
 * callers would otherwise each re-derive as `message.system ? …` and only some
 * would remember to.
 */
export function authorNameFor(message: Message, ctx: AuthorContext): string | undefined {
  if (message.system) return undefined;
  // "You" only for me (AI-3136): our side can also hold the company or a colleague.
  if (message.senderId === ctx.currentUserId) return 'You';
  return ctx.isOwnMessage(message) ? ourSideAuthor(message) : ctx.counterpartName;
}

export interface AuthorContext {
  isOwnMessage: (message: Message) => boolean;
  currentUserId: string;
  counterpartName: string;
}

/** Someone else on our side: the company that held the thread before the
 *  cutover, or a colleague after a reassignment. Their own name when we have it. */
export function ourSideAuthor(message: Message): string {
  return (
    message.senderName || (message.senderId.startsWith('emp_') ? 'A colleague' : 'Your company')
  );
}

interface MessageQuoteProps {
  quoted: QuotedPreview;
  onJump: (id: MessageId) => void;
}

/**
 * The message being answered, sitting above the answer.
 *
 * A button, not a decoration: tapping it takes the reader to the original. That
 * is the whole reason to store an id rather than a copy of the text, and the
 * reason the strip is worth its height. Two lines maximum, because a quote
 * taller than the reply it belongs to has stopped being a reference.
 */
export function MessageQuote({ quoted, onJump }: MessageQuoteProps) {
  return (
    <button
      type="button"
      className={styles.quote}
      onClick={() => onJump(quoted.id)}
      aria-label={`Go to the quoted message${quoted.authorName ? ` from ${quoted.authorName}` : ''}`}
    >
      {quoted.authorName && <span className={styles.author}>{quoted.authorName}</span>}
      <span className={styles.text}>{quoted.text}</span>
    </button>
  );
}

interface QuotedDraftProps {
  quoted: QuotedPreview;
  onCancel: () => void;
}

/**
 * The same quote, staged above the composer while the reply is being written.
 *
 * A sibling of `MessageQuote` rather than a mode of it, for a dull but binding
 * reason: this one holds a dismiss button, and a button inside a button is
 * invalid. They share the stylesheet, which is where the resemblance that
 * matters actually lives — the reader should recognise what they staged when it
 * lands in the thread.
 *
 * It does not jump anywhere. The original is a few pixels above and the reader
 * put it there a second ago; the useful control here is the one that takes it
 * back.
 */
export function QuotedDraft({ quoted, onCancel }: QuotedDraftProps) {
  return (
    <div className={styles.draft}>
      {/* Leading, not trailing. It is the only control on the strip and the only
          thing here anybody clicks; putting it first means the cursor does not
          have to cross a line of text that grows and shrinks with whatever is
          being quoted. */}
      <button
        type="button"
        className={styles.cancel}
        onClick={onCancel}
        title={quoted.editing ? 'Cancel edit' : 'Cancel reply'}
        aria-label={quoted.editing ? 'Cancel edit' : 'Cancel reply'}
      >
        <Icon name="close" size={15} />
      </button>
      <span className={styles.draftDivider} aria-hidden="true" />
      <span className={styles.draftLine}>
        <span className={styles.draftLabel}>
          {quoted.editing ? 'Edit message' : `Reply to ${quoted.authorName ?? 'this message'}`}:
        </span>{' '}
        {quoted.text}
      </span>
    </div>
  );
}
